import React, { useState, useEffect } from 'react';
import { getCampuses, addCampus, updateCampus, deleteCampus } from '../api';
import { usePermissions } from '../utils/permissions';

function Campuses() {
  const { can } = usePermissions();
  const [campuses, setCampuses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingCampus, setEditingCampus] = useState(null);
  const [formData, setFormData] = useState({ name: '', code: '' });
  const [message, setMessage] = useState({ type: '', text: '' });

  useEffect(() => {
    fetchCampuses();
  }, []);

  const fetchCampuses = async () => {
    try {
      const response = await getCampuses();
      setCampuses(response.data);
    } catch (error) {
      showMessage('error', 'Failed to load campuses');
    } finally {
      setLoading(false);
    }
  };

  const showMessage = (type, text) => {
    setMessage({ type, text });
    setTimeout(() => setMessage({ type: '', text: '' }), 3000);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingCampus) {
        await updateCampus(editingCampus.id, formData);
        showMessage('success', 'Campus updated successfully');
      } else {
        await addCampus(formData);
        showMessage('success', 'Campus added successfully');
      }
      setShowModal(false);
      setFormData({ name: '', code: '' });
      setEditingCampus(null);
      fetchCampuses();
    } catch (error) {
      showMessage('error', error.response?.data?.error || 'Operation failed');
    }
  };

  const handleEdit = (campus) => {
    setEditingCampus(campus);
    setFormData({ name: campus.name, code: campus.code });
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this campus?')) {
      try {
        await deleteCampus(id);
        showMessage('success', 'Campus deleted successfully');
        fetchCampuses();
      } catch (error) {
        showMessage('error', 'Failed to delete campus');
      }
    }
  };

  const openAddModal = () => {
    setEditingCampus(null);
    setFormData({ name: '', code: '' });
    setShowModal(true);
  };

  if (loading) {
    return <div className="loading"><div className="spinner"></div></div>;
  }

  return (
    <div>
      <div className="card">
        <div className="card-header">
          <h2 className="card-title">Campuses</h2>
          {can('campuses', 'create') && (
            <button className="btn btn-primary" onClick={openAddModal}>
              Add Campus
            </button>
          )}
        </div>

        {message.text && (
          <div className={`alert alert-${message.type}`}>
            {message.text}
          </div>
        )}

        <div className="table-container">
          <table className="table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Code</th>
                <th>Created At</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {campuses.length === 0 ? (
                <tr>
                  <td colSpan="4" style={{ textAlign: 'center', padding: '20px' }}>
                    No campuses found. Add your first campus!
                  </td>
                </tr>
              ) : (
                campuses.map((campus) => (
                  <tr key={campus.id}>
                    <td>{campus.name}</td>
                    <td>{campus.code}</td>
                    <td>{new Date(campus.created_at).toLocaleDateString()}</td>
                    <td>
                      <div className="action-buttons">
                        {can('campuses', 'edit') && (
                          <button
                            className="btn btn-small btn-secondary"
                            onClick={() => handleEdit(campus)}
                          >
                            Edit
                          </button>
                        )}
                        {can('campuses', 'delete') && (
                          <button
                            className="btn btn-small btn-danger"
                            onClick={() => handleDelete(campus.id)}
                          >
                            Delete
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3 className="modal-title">
                {editingCampus ? 'Edit Campus' : 'Add Campus'}
              </h3>
              <button className="modal-close" onClick={() => setShowModal(false)}>
                &times;
              </button>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label className="form-label">Campus Name *</label>
                <input
                  type="text"
                  className="form-input"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                />
              </div>
              <div className="form-group">
                <label className="form-label">Campus Code *</label>
                <input
                  type="text"
                  className="form-input"
                  value={formData.code}
                  onChange={(e) => setFormData({ ...formData, code: e.target.value })}
                  required
                  maxLength="10"
                />
                <small style={{ color: '#666', fontSize: '12px' }}>
                  Example: 01, 02, 03 (2 digits recommended)
                </small>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setShowModal(false)}>
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary">
                  {editingCampus ? 'Update' : 'Add'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default Campuses;
